import React, { useMemo } from 'react';
import { Spin, Alert } from 'antd';
import { useHomeDashboardData } from '@/hooks/useHomeDashboardData';
import { useAuthStore } from '@/store/authStore';
import {
  ExecutiveHeader,
  ExecutivePriorityStrip,
  ExecutiveKpiRow,
  ExecutiveQuickActions,
  ExecutiveTrendsRow,
  ExecutiveActivityPanels,
  ExecutiveProjectAndCustomers,
  ExecutiveDomainHub,
} from '@/components/Dashboard/executive';

/**
 * Former Company Dashboard (executive collage). Reachable under Operational Overview.
 */
export const CompanyOverviewPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const { data, loading, error, updatedAt, refresh } = useHomeDashboardData();

  const displayName = useMemo(() => {
    const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
    if (name) return name;
    return user?.username || user?.email || 'User';
  }, [user]);

  const companyName = (user as unknown as { company_name?: string })?.company_name;

  if (loading && !data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          message="Company overview unavailable"
          description={error || 'Could not load data.'}
          action={
            <a onClick={() => refresh()} style={{ cursor: 'pointer' }}>
              Retry
            </a>
          }
        />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Partial load"
          description={error}
          closable
        />
      )}

      <ExecutiveHeader
        displayName={displayName}
        companyName={typeof companyName === 'string' ? companyName : undefined}
        updatedAt={updatedAt}
        loading={loading}
        onRefresh={refresh}
      />

      <ExecutivePriorityStrip unified={data.unified} />

      <ExecutiveKpiRow statistics={data.statistics} />

      <ExecutiveQuickActions />

      <ExecutiveTrendsRow
        revenueTrend={data.revenueTrend}
        salesByType={data.salesByType}
        loading={loading}
      />

      <ExecutiveActivityPanels activities={data.activities} pendingApprovals={data.pendingApprovals} />

      <ExecutiveProjectAndCustomers
        projectStatus={data.projectStatus}
        topCustomers={data.topCustomers}
        loading={loading}
      />

      <ExecutiveDomainHub />
    </div>
  );
};

export default CompanyOverviewPage;
